import { FC, useState } from 'react';
import {
  Box,
  Card, CardContent, CardHeader,
  Collapse,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CCHChart from '@/components/CCHChart';
import { useTranslation } from 'react-i18next';
import useAuth from '@/components/Authentication/hooks/useAuth';
import { useContracts } from '../../hooks/useContracts';
import { ContractDisplayTypes } from '../../types/contracts';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import { ExpandLess, ExpandMore, HighlightOff, InfoOutlined } from '@mui/icons-material';
import CCHExtraInfo from '../../ContractDetail/CCHExtraInfo';

interface IConsumptionDetailsPanelProps {
  type: ContractDisplayTypes;
}

export const ConsumptionDetailsPanel: FC<IConsumptionDetailsPanelProps> = ({
  type
}) => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const { token } = useAuth();
  const { currentContract: contract } = useContracts(type);

  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const [ showCCHInfo, setShowCCHInfo] = useState(false);
  const [ showCCHMoreInfo, setShowCCHMoreInfo] = useState(false);
  const [ chartComponents, setChartComponents] = useState({});

  return contract && <Card style={{marginBottom: 3}} id="consumption">
    <CardHeader
      title={
        <Box display="flex" alignItems={'flex-end'}>
          <Typography variant="h6">
            {t('common:text.contract_consumptions')}
          </Typography>
          { Settings?.contract?.enableInfoCCH &&
            <IconButton
              size='small'
              onClick={() => setShowCCHInfo(!showCCHInfo)}
            >
              {showCCHInfo ?
                <HighlightOff fontSize='small' color='error'/>
              :
                <InfoOutlined fontSize='small' color='primary'/>
              }
            </IconButton>
          }
        </Box>
      }
    />
    <CardContent sx={{padding: '0px 16px 16px 16px'}}>
      <Collapse in={showCCHInfo} timeout="auto" unmountOnExit>
        <Typography variant="subtitle1" mb={2}>
          {t('common:chartLegend.info_consum')}
        </Typography>
      </Collapse>
      <CCHChart
        contractId={contract.name} 
        token={token}
        tariff={contract.tariff.name}
        isPeninsula={contract.is_peninsula ?? true}
        chartComponents={chartComponents}
        setChartComponents={setChartComponents}
      />
      {(breakpointDownMd &&
        Object.keys(chartComponents).length > 0 &&
        Settings?.contract?.enableInfoCCH
      ) &&
        <>
          <Box display="flex" alignItems={'flex-end'}>
            <Typography variant="subtitle1">
              {t('common:chartLegend.show_more_info')}
            </Typography>
            <IconButton
              size='small'
              onClick={() => setShowCCHMoreInfo(!showCCHMoreInfo)}
            >
              {showCCHMoreInfo ?
                <ExpandLess fontSize='small'/>
              :
                <ExpandMore fontSize='small'/>
              }
            </IconButton>
          </Box>
          <Collapse in={showCCHMoreInfo} timeout="auto" unmountOnExit>
            <CCHExtraInfo components={chartComponents} />
          </Collapse>
        </>
      }
    </CardContent>
  </Card>
}
  